/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosError,
  AxiosResponse,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from "axios";
import { decryptData } from "./encryption";
import appConfig from "./config";

const ISSERVER = typeof window === "undefined";

const NO_REDIRECT_ROUTES = ["/auth/"];

export const NETWORK_ERROR_MESSAGE =
  "Network error. Please check your connection and try again.";

const isNoRedirectRoute = () =>
  !ISSERVER &&
  NO_REDIRECT_ROUTES.some((path) => window.location.pathname.includes(path));

const getStoredToken = (): string | null => {
  if (ISSERVER) return null;

  const token = localStorage.getItem("token");
  if (!token) return null;

  try {
    const decrypted = decryptData(token, appConfig.secretKey);
    if (decrypted) return decrypted;
  } catch {
  }

  localStorage.removeItem("token");
  return null;
};

const logout = () => {
  if (ISSERVER) return;

  const fromPath = window.location.pathname + window.location.search;
  localStorage.clear();
  localStorage.setItem("fromPath", fromPath);
  window.location.href = "/auth/login";
};

export const resourceReqInterceptor = async (
  config: InternalAxiosRequestConfig
) => {
  const modifiedConfig = { ...config };

  const token = getStoredToken();

  if (token && modifiedConfig.headers) {
    modifiedConfig.headers.Authorization = `Bearer ${token}`;
  }

  return modifiedConfig;
};

export const resourceResInterceptor = (response: AxiosResponse) => {
  if (response.data instanceof Blob) {
    return response.data as unknown as AxiosResponse;
  }

  return { ...response.data } as AxiosResponse;
};

export const resourceResErrorInterceptor = async (error: AxiosError) => {
  const body = error?.response?.data as IErrorResponse | undefined;

  if (error.code === AxiosError.ERR_NETWORK || !error.response) {
    return Promise.reject({ message: NETWORK_ERROR_MESSAGE });
  }

  if (error.response.status === 401 && !isNoRedirectRoute() && error.config) {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    void originalRequest;

    logout();
    return new Promise(() => {});
  }

  if (error.response.status === 403) {
    return Promise.reject({
      ...(body ?? {}),
      isForbidden: true,
      message:
        body?.message ?? "You do not have permission to perform this action.",
    });
  }

  return Promise.reject({
    ...(body ?? {}),
    message: body?.message ?? "Something went wrong. Please try again.",
  });
};

const baseConfig: CreateAxiosDefaults = {
  baseURL: appConfig.baseUrl,
  headers: {
    "content-type": "application/json",
    Accept: "*/*",
  },
};

const server = axios.create(baseConfig);

export const serverWithoutInterceptor = axios.create(baseConfig);

export default server;

server.interceptors.request.use(resourceReqInterceptor);
server.interceptors.response.use(
  resourceResInterceptor,
  resourceResErrorInterceptor
);
